import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { FileUploader } from '@/common/components/FileUploader'
import MaskedTextField from '@/common/components/MaskedTextField'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import useCompany from '@/common/hooks/queries/useCompany'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency, formatDateRange } from '@/common/utils/formatters'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { useCompanyBranch } from '@/features/privateCompanies/hooks/useCompanyBranch'
import { EntryGovernmentAllocationDetails } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/components/EntryGovernmentAllocationDetails'
import { CompanyGovernmentAllocationEntryData } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist'
import { CalendarDaysIcon, SearchIcon } from 'lucide-react'
import { useParams } from 'react-router-dom'

export const GovernmentAllocationEntryCompletedForm = ({
  companyGovernmentAllocation,
}: {
  companyGovernmentAllocation: CompanyGovernmentAllocationEntryData
}) => {
  const { companyId } = useParams()

  const { data: company } = useCompany({ id: companyId || '' })

  const { data: branchData } = useCompanyBranch(companyGovernmentAllocation?.government_allocation?.branch_id || '')

  return (
    <Box className="flex gap-6 items-start h-[calc(100vh-130px)] shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
      <div className="w-full flex flex-col gap-6 h-[calc(100vh-178px)] overflow-y-auto">
        <p className="text-lg font-bold">
          Detalhes {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
        </p>

        <div className="flex gap-6">
          <div>
            <p className="text-sm font-light italic">Valor global do contrato</p>
            <p className="text-lg font-bold">{formatCurrency(Number(company?.granted_credit || 0))}</p>
          </div>

          <div>
            <p className="text-sm font-light italic">Cota de empenho da filial</p>
            <p className="text-lg font-bold">{formatCurrency(Number(branchData?.granted_credit || 0))}</p>
          </div>
        </div>

        {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? (
          <div className="flex gap-x-2">
            <TextField
              label="Número do ofício do Empenho"
              className="mt-5"
              name="office_number"
              defaultValue={companyGovernmentAllocation?.office_number}
              disabled
            />

            <div className="space-y-2">
              <p className="text-[11px] leading-3">Período de competência</p>

              <div className="flex items-start gap-x-2">
                <MaskedTextField
                  label="Início"
                  mask="00/00/0000"
                  LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                  name="government_allocation_attributes.start_of_document_validity"
                  defaultValue={
                    companyGovernmentAllocation?.government_allocation?.start_of_document_validity
                      ? dateTransformer(companyGovernmentAllocation?.government_allocation?.start_of_document_validity)
                      : undefined
                  }
                  disabled
                />

                <MaskedTextField
                  label="Fim"
                  mask="00/00/0000"
                  LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                  name="government_allocation_attributes.end_of_document_validity"
                  defaultValue={
                    companyGovernmentAllocation?.government_allocation?.end_of_document_validity
                      ? dateTransformer(companyGovernmentAllocation?.government_allocation?.end_of_document_validity)
                      : undefined
                  }
                  disabled
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div>
              <p className="text-sm font-light italic">N da cota estimativa</p>
              <p className="text-lg font-bold">{companyGovernmentAllocation?.office_number}</p>
            </div>

            <div>
              <p className="text-sm font-light italic">Período de competência</p>
              <p className="text-lg font-bold">
                {companyGovernmentAllocation?.government_allocation?.start_of_document_validity &&
                  companyGovernmentAllocation?.government_allocation?.end_of_document_validity &&
                  formatDateRange(
                    new Date(companyGovernmentAllocation?.government_allocation?.start_of_document_validity),
                    new Date(companyGovernmentAllocation?.government_allocation?.end_of_document_validity),
                  )}
              </p>
            </div>
          </>
        )}

        {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' && (
          <FileUploader
            name="file"
            accept="application/pdf, image/png, image/jpg, image/jpeg"
            title="Anexe o Empenho"
            fileName={
              companyGovernmentAllocation?.file?.url
                ? getFilenameFromUrl(companyGovernmentAllocation?.file?.url)
                : undefined
            }
            currentFileUrl={companyGovernmentAllocation?.file?.url ? companyGovernmentAllocation?.file?.url : undefined}
            disabled
          />
        )}

        <CurrencyTextField
          label={`Valor total ${companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do empenho' : 'da cota'
            }`}
          markAsRequired
          maxValue={999_999_999.99}
          className="w-full"
          name="total_value"
          defaultValue={
            companyGovernmentAllocation?.total_value
              ? formatCurrency(Number(companyGovernmentAllocation?.total_value))
              : undefined
          }
          disabled
        />

        <Accordion
          className="py-1"
          head={
            <p>
              Detalhamento{' '}
              {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
            </p>
          }
          body={
            <div className="space-y-4">
              <Select
                placeholder={
                  <div className="flex items-center gap-x-2">
                    <SearchIcon />
                    <p>Pesquisar serviços</p>
                  </div>
                }
                value=""
                isDisabled
              />

              {!!companyGovernmentAllocation.entry_product_categories.length && (
                <EntryGovernmentAllocationDetails
                  companyBranchId={branchData?.id}
                  governmentAllocationId={companyGovernmentAllocation.id}
                  entryProductCategories={companyGovernmentAllocation.entry_product_categories}
                  disabled
                  hideButtons
                  controlledBy={companyGovernmentAllocation?.government_allocation?.controlled_by}
                />
              )}
            </div>
          }
        />
      </div>

      <Checklist
        requirement_list_summary={companyGovernmentAllocation?.requirement_list_summary}
        className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
      />
    </Box>
  )
}
